import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../services/supabase';
import { signupUser, linkAnonymousUser } from '../services/credits';
import { getOrCreateAnonId, clearAnonId } from '../utils/anonId';
import { usePostHog } from 'posthog-js/react';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>('Signing you in...');
  const posthog = usePostHog();

  useEffect(() => {
    const handleCallback = async () => {
      const startTime = Date.now();

      // Detect mobile and browser type
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
      const isFacebookBrowser = /FBAN|FBAV/i.test(navigator.userAgent);
      const isMobileFacebook = isMobile && isFacebookBrowser;

      // Track if user was on a critical page before redirect (REVENUE PROTECTION)
      const referrer = document.referrer;
      const wasOnUpgradePage = referrer.includes('/upgrade');
      const wasOnCreditsPage = referrer.includes('/credits');
      const wasOnCheckoutPage = referrer.includes('checkout');
      const wasOnCriticalPage = wasOnUpgradePage || wasOnCreditsPage || wasOnCheckoutPage;

      if (wasOnCriticalPage) {
        posthog.capture('unexpected_oauth_redirect', {
          from_page: referrer,
          is_critical_page: true,
          page_type: wasOnUpgradePage ? 'upgrade' : wasOnCreditsPage ? 'credits' : 'checkout',
          user_agent: navigator.userAgent,
          is_mobile: isMobile,
          timestamp: new Date().toISOString(),
        });

        console.error('❌ [AuthCallback] CRITICAL: User redirected from checkout page!', {
          from: referrer,
          to: window.location.href,
        });
      }

      try {
        const supabase = getSupabaseClient();

        // Check for error in URL hash (email confirmation errors and OAuth errors)
        const hash = window.location.hash;
        if (hash.includes('error=')) {
          const errorParams = new URLSearchParams(hash.substring(1));
          const error = errorParams.get('error');
          const errorCode = errorParams.get('error_code');
          const errorDescription = errorParams.get('error_description');

          console.error('❌ [Auth] Error in URL hash:', error);
          console.error('❌ [Auth] Error code:', errorCode);
          console.error('❌ [Auth] Error description:', errorDescription);

          // Handle bad_oauth_state (stale OAuth callback)
          if (errorCode === 'bad_oauth_state') {
            console.error('❌ [Auth] OAuth state mismatch - likely stale callback');

            // Clean up URL
            window.history.replaceState(null, '', window.location.pathname + window.location.search);

            // Clear all OAuth flags
            sessionStorage.removeItem('genanything_oauth_initiated');
            sessionStorage.removeItem('genanything_oauth_provider');
            sessionStorage.removeItem('oauth_initiated_time');
            sessionStorage.removeItem('oauth_provider');

            // Redirect to saved path or home
            const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
            navigate(savedPath + '?error=oauth_expired&message=' + encodeURIComponent('OAuth session expired. Please try signing in again.'), { replace: true });
            return;
          }

          // Handle other errors (email confirmation, etc.)
          // Clear the error hash from URL
          window.history.replaceState(null, '', window.location.pathname + window.location.search);

          // Redirect to login with error message
          navigate('/login?error=email_expired&message=' + encodeURIComponent(errorDescription || 'Email link is invalid or has expired. Please request a new confirmation email.'), { replace: true });
          return;
        }

        // Reduce wait time on mobile (especially Facebook browser)
        const initialWaitTime = isMobileFacebook ? 100 : isMobile ? 200 : 500;
        await new Promise((resolve) => setTimeout(resolve, initialWaitTime));

        const { data: { session } } = await supabase.auth.getSession();

        // CRITICAL: Check for stale OAuth callbacks BEFORE processing
        const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
        const isRecentOAuth = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 30000; // 30 seconds
        const isVeryRecentOAuth = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 120000; // 2 minutes grace period

        // Add fallback: if there's an access_token in the URL, OAuth just completed successfully
        // Reuse hash variable declared earlier (line 50)
        const hasAccessTokenInUrl = hash.includes('access_token=');

        // OAuth is legitimate if:
        // 1. Recent (< 30s), OR
        // 2. Has valid access token AND within grace period (< 2min) AND no existing session
        const isLikelyLegitimate = isRecentOAuth || (hasAccessTokenInUrl && isVeryRecentOAuth && !session);

        // Track timing check for debugging
        posthog.capture('oauth_callback_timing_check', {
          is_recent: isRecentOAuth,
          is_very_recent: isVeryRecentOAuth,
          oauth_age_ms: oauthInitiatedTime ? Date.now() - parseInt(oauthInitiatedTime) : null,
          has_session: !!session,
          has_access_token: hasAccessTokenInUrl,
          decision: isLikelyLegitimate ? 'accepted' : 'rejected',
        });

        // If OAuth is stale AND user already authenticated, reject immediately
        // Changed from OR to AND - we should only reject if BOTH conditions are true
        // A legitimate OAuth signup will have isLikelyLegitimate=true AND a new session
        if (!isLikelyLegitimate && (session && !session.user.is_anonymous)) {
          console.warn('❌ [AuthCallback] Rejecting stale OAuth callback', {
            isRecentOAuth,
            isVeryRecentOAuth,
            hasAccessToken: hasAccessTokenInUrl,
            isAuthenticated: !!session,
            oauthAge: oauthInitiatedTime ? Date.now() - parseInt(oauthInitiatedTime) : 'N/A',
          });

          // Clean up all OAuth flags
          sessionStorage.removeItem('genanything_oauth_initiated');
          sessionStorage.removeItem('genanything_oauth_provider');
          sessionStorage.removeItem('oauth_initiated_time');
          sessionStorage.removeItem('oauth_provider');

          // Clean URL
          window.history.replaceState(null, '', window.location.pathname + window.location.search);

          // Redirect to saved path or home
          const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
          navigate(savedPath, { replace: true });

          posthog.capture('rejected_stale_oauth_callback', {
            reason: !isLikelyLegitimate ? 'oauth_too_old' : 'user_already_authenticated',
            oauth_age_ms: oauthInitiatedTime ? Date.now() - parseInt(oauthInitiatedTime) : null,
            redirected_to: savedPath,
            had_access_token: hasAccessTokenInUrl,
          });

          return; // EXIT EARLY - don't process callback
        }

        // DEBUG: Log session data for OAuth troubleshooting
        console.log('🔍 [AuthCallback] Session data:', {
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email,
          isAnonymous: session?.user?.is_anonymous,
          provider: session?.user?.app_metadata?.provider,
          userMetadata: session?.user?.user_metadata,
          fullSession: session
        });

        if (session) {
          // Handle authenticated user signup (non-anonymous)
          if (!session.user.is_anonymous) {
            // CRITICAL: Handle cases where email might be missing
            // Store email in properly typed variable for TypeScript
            let userEmail: string = session.user.email || '';

            if (!userEmail) {
              console.error('❌ [AuthCallback] OAuth provider did not provide email!', {
                provider: session.user.app_metadata?.provider,
                userId: session.user.id,
                userMetadata: session.user.user_metadata
              });

              // Try to get email from user_metadata as fallback
              const emailFromMetadata = session.user.user_metadata?.email;
              if (!emailFromMetadata) {
                setStatus('Error: Email not provided by OAuth provider');
                await new Promise((resolve) => setTimeout(resolve, 3000));
                navigate('/?error=no_email', { replace: true });
                return;
              }

              console.log('✅ [AuthCallback] Found email in user_metadata, using it');
              userEmail = emailFromMetadata;
              session.user.email = emailFromMetadata;
            }

            setStatus('Setting up your account...');

            try {
              // Check if we have an anonymous user ID to link
              const anonymousId = getOrCreateAnonId();
              const hasAnonymousId = anonymousId && localStorage.getItem('genanything_anon_id');

              // Run API calls in parallel for mobile performance
              let linkResult = null;
              let signupResult = null;
              let isNewUser = false;

              if (hasAnonymousId) {
                // Try to link anonymous user AND signup in parallel
                setStatus('Linking your account...');

                console.log('📝 [AuthCallback] Calling linkAnonymousUser and signupUser with:', {
                  anonymousId,
                  email: userEmail,
                  userId: session.user.id,
                  fullName: session.user.user_metadata?.full_name
                });

                const results = await Promise.allSettled([
                  linkAnonymousUser(anonymousId, session.user.id, userEmail),
                  signupUser(userEmail, session.user.id, session.user.user_metadata?.full_name)
                ]);

                // Process link result
                if (results[0].status === 'fulfilled') {
                  linkResult = results[0].value;
                  if (linkResult.signup_bonus_granted) {
                    setStatus('Welcome! You received 100 free credits!');
                    clearAnonId();
                  } else if (linkResult.assets_transferred > 0) {
                    setStatus('Account linked successfully!');
                    clearAnonId();
                  }
                } else {
                  console.warn('⚠️ [Auth] Failed to link anonymous user (may not exist):', results[0].reason);
                }

                // Process signup result
                if (results[1].status === 'fulfilled') {
                  signupResult = results[1].value;
                  isNewUser = signupResult.signup_bonus_granted === true;
                  console.log('✅ [AuthCallback] signupUser succeeded:', {
                    signup_bonus_granted: signupResult.signup_bonus_granted,
                    credits: signupResult.credits
                  });
                } else {
                  console.error('❌ [AuthCallback] Signup API failed:', {
                    status: results[1].status,
                    reason: results[1].reason,
                    reasonString: JSON.stringify(results[1].reason, null, 2)
                  });

                  // Track signup failure
                  posthog.capture('signup_api_failed', {
                    error: results[1].reason instanceof Error ? results[1].reason.message : String(results[1].reason),
                    user_id: session.user.id,
                    email: userEmail,
                  });

                  isNewUser = false;
                }
              } else {
                // No anonymous ID - just signup
                setStatus('Completing signup...');

                console.log('📝 [AuthCallback] Calling signupUser (no anonymous ID) with:', {
                  email: userEmail,
                  userId: session.user.id,
                  fullName: session.user.user_metadata?.full_name
                });

                try {
                  signupResult = await signupUser(
                    userEmail,
                    session.user.id,
                    session.user.user_metadata?.full_name
                  );

                  isNewUser = signupResult.signup_bonus_granted === true;
                  console.log('✅ [AuthCallback] signupUser succeeded:', {
                    signup_bonus_granted: signupResult.signup_bonus_granted,
                    credits: signupResult.credits
                  });
                } catch (signupApiError) {
                  console.error('❌ [AuthCallback] Signup API error:', {
                    error: signupApiError,
                    errorMessage: signupApiError instanceof Error ? signupApiError.message : String(signupApiError),
                    errorStack: signupApiError instanceof Error ? signupApiError.stack : undefined
                  });

                  // Track signup failure
                  posthog.capture('signup_api_failed', {
                    error: signupApiError instanceof Error ? signupApiError.message : String(signupApiError),
                    user_id: session.user.id,
                    email: userEmail,
                  });

                  isNewUser = false;
                }
              }

              // NOTE: Signup/login events are now tracked in AuthContext.tsx via onAuthStateChange

              if (isNewUser && signupResult?.credits) {
                setStatus(`Welcome! You received ${signupResult.credits} free credits!`);

                // Skip or reduce success message delay on mobile
                const successWaitTime = isMobileFacebook ? 0 : isMobile ? 500 : 2000;
                if (successWaitTime > 0) {
                  await new Promise((resolve) => setTimeout(resolve, successWaitTime));
                }
              }
            } catch (signupError) {
              console.error('⚠️ [Auth] Signup error (user may already exist):', signupError);
              // Continue even if signup fails (user might already exist)
            }
          }

          // Track callback completion
          const duration = Date.now() - startTime;

          posthog.capture('oauth_callback_completed', {
            duration_ms: duration,
            is_mobile: isMobile,
            is_facebook_browser: isFacebookBrowser,
            user_agent: navigator.userAgent,
          });

          if (duration > 5000) {
            posthog.capture('oauth_callback_delayed', {
              duration_ms: duration,
              is_mobile: isMobile,
              is_facebook_browser: isFacebookBrowser,
            });
          }

          // Redirect to intended destination or default
          // Try multiple sources for reliability (URL param, sessionStorage, localStorage backup, fallback)
          let redirectTo =
            searchParams.get('redirect_to') ||               // Primary: URL parameter
            sessionStorage.getItem('oauth_return_path') ||   // Backup 1: SessionStorage
            null;

          // If sessionStorage and URL param both failed, try localStorage backup
          if (!redirectTo || redirectTo === '/') {
            const localStoragePath = localStorage.getItem('oauth_return_path_backup');
            const localStorageTimestamp = localStorage.getItem('oauth_return_path_timestamp');

            if (localStoragePath && localStorageTimestamp) {
              const age = Date.now() - parseInt(localStorageTimestamp);
              // Only use if less than 5 minutes old
              if (age < 300000) {
                redirectTo = localStoragePath;
                console.log('✅ [AuthCallback] Using localStorage backup for redirect:', redirectTo, 'age:', age, 'ms');
              } else {
                console.log('⚠️ [AuthCallback] localStorage backup too old:', age, 'ms, ignoring');
              }
            }
          }

          // Final fallback
          if (!redirectTo || redirectTo === '/auth/callback') {
            redirectTo = '/';
          }

          console.log('🔄 [AuthCallback] Redirecting to:', redirectTo);

          // Clear all backup storage after use
          if (sessionStorage.getItem('oauth_return_path')) {
            sessionStorage.removeItem('oauth_return_path');
          }
          localStorage.removeItem('oauth_return_path_backup');
          localStorage.removeItem('oauth_return_path_timestamp');
          localStorage.removeItem('oauth_return_url_backup');

          // CRITICAL: Clear callback page from browser history to prevent back button navigation
          // This prevents users from accidentally navigating back to /auth/callback
          window.history.replaceState(null, '', redirectTo);

          navigate(redirectTo, { replace: true });
        } else {
          // No session, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error handling auth callback:', error);
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, searchParams, posthog]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">{status}</p>
      </div>
    </div>
  );
}

